import { DataCategories, DiagnosticLevel, EventFlags, SamplingPolicy, TelemetrySink } from '@microsoft/oteljs'
import {
  ExpirationDate,
  IOTelLoggerOptions,
  IUserContext,
  OTelLogger} from '@notes/aria-common-schema-logger'
import { isEdgeHubQueryParamPresent } from '../../lib/urlParserUtils'
import { OTelTokens } from './constants'
import { EventName } from './EventName'
import { eventProperties } from './eventProperties'
import {
  INotesIntegrationLogItem,
  INotesIntegrationOTelLoggerOptions,
  INotesIntegrationUnexpectedLogItem,
  IONDCLogger
  } from './types'
import { buildContext } from './utils'

export class NotesIntegrationCommonLogger implements IONDCLogger {
  private hostApp?: string
  private otelLogger: OTelLogger
  private isInitialized: boolean

  constructor(
    options: INotesIntegrationOTelLoggerOptions,
    userInfo?: IUserContext | undefined,
    sink?: TelemetrySink | undefined) {
    const { appName, ...rest } = options
    const isEUDBRoutingEnabled = true

    const otelLoggerOptions: IOTelLoggerOptions = {
      ...rest,
      appID: appName,
      tenantID: OTelTokens.ONDC_NOTES_INTEGRATION_OTEL_TENANT,
      tenantNameSpace: 'Office.OneNoteIntegrations.OneNoteDotComNotesIntegration',
      nexusTenantToken: OTelTokens.ONENOTE_NEXUS_TENANT_TOKEN,
      transmitProfile: 'NEAR_REAL_TIME',
      /**
       * Office OneNoteIntegrations has been onboarded to HRD as part of the work item below.
       * VSO: https://office.visualstudio.com/OC/_workitems/edit/9171957
       */
      officeApplicationNumber: 164,
    }
    this.otelLogger = new OTelLogger(
      otelLoggerOptions,
      buildContext(),
      isEUDBRoutingEnabled,
      userInfo,
      sink,
      undefined,
      false
      // We need not enable logging of Optional events for ONDC since each event in ONDC is explicitly marked as RequiredServiceData
    )

    if (sink) {
      this.isInitialized = true
    }

    if (!this.isInitialized) {
      // This will ensure that the telemetry is routed to the default
      // data boundary till the user context is set.
      this.otelLogger.flushLogs()
      this.isInitialized = true
    }
    this.hostApp = isEdgeHubQueryParamPresent() ? 'EdgeHub' : undefined
  }

  setAgaveHostApp(hostApp: string): void {
    this.hostApp = hostApp
  }

  logONDCReportData(eventName: EventName, item: INotesIntegrationLogItem): void {
    const { Context, ...otherProperties } = item
    const finalContext = buildContext(Context)
    const eventProps = eventProperties[eventName]
    const eventFlags = this.buildEventFlags()
    const finalItem = {
      ...otherProperties,
      Context: finalContext,
      ExpirationDate: ExpirationDate.Perpetual,
      IsExportable: !!(eventProps && eventProps.isExportable),
      HostApp: this.hostApp,
    }

    this.otelLogger.logReportData(finalItem, eventFlags)
  }

  logONDCUnexpected(logItem: INotesIntegrationUnexpectedLogItem): void {
    const { Context, ...otherProperties } = logItem
    const finalContext = buildContext(Context)
    const eventFlags = this.buildEventFlags()
    const item = {
      ...otherProperties,
      Context: finalContext,
      ExpirationDate: ExpirationDate.Perpetual,
      IsExportable: false, // We don't export errors
      HostApp: this.hostApp,
    }
    this.otelLogger.logUnexpected(item, eventFlags)
  }

  setUserContext(otelUserContext: IUserContext): void {
    this.otelLogger.setUserContext(otelUserContext)
    .then(() => {
      // After setting the user context, a new OTel sink will be created
      // if the existing sink was an instance of OneDSSink. This is done
      // to ensure that the telemetry is routed to the correct data boundary.
      // The current OTel sink will be shut down thereafter.
      // If the existing sink was an instance of Office.js Sink, then continue
      // using the same sink, which correctly routes telemetry to EU/ROW, and
      // is hence EUDB compliant.
      this.isInitialized = true
    })
    .catch ((error: Error) => {
      // tslint:disable-next-line:no-console
      console.warn('Failed to set the user Context ' + error)
    })
  }

  private buildEventFlags(): Partial<EventFlags> {
    /**
     * We checked with our privacy drivers and have received an approval
     * to change the diagnostic level to RequiredServiceData
     * given that OneNoteDotComNotesIntegration is a web based experience.
     */
    return {
      dataCategories: DataCategories.ProductServiceUsage,
      diagnosticLevel: DiagnosticLevel.RequiredServiceData,
      samplingPolicy: SamplingPolicy.CriticalUsage,
    }
  }
}
